import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedNumber } from '@/components/global';
import { ProductIdentity, useProductCatalog } from '@/components/products';
import { WarehouseAssortmentBulkAddDialog } from './WarehouseAssortmentBulkAddDialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { Loader2, Plus, Trash2 } from 'lucide-react';

const ADD_BATCH_SIZE = 500;

function chunkIds(ids: string[], size: number): string[][] {
  const batches: string[][] = [];
  for (let index = 0; index < ids.length; index += size) {
    batches.push(ids.slice(index, index + size));
  }
  return batches;
}

export interface WarehouseAssortmentItem {
  id: string;
  productId: string;
  quantityOnHand: number;
  quantityReserved: number;
  product?: {
    id: string;
    sku: string;
    name: string;
    imageUrl?: string | null;
    media?: Array<{ url: string; mediaType: string }> | null;
  };
}

export interface WarehouseAssortmentPanelProps {
  warehouseId: string;
  canEdit?: boolean;
}

export function WarehouseAssortmentPanel({
  warehouseId,
  canEdit = true,
}: WarehouseAssortmentPanelProps) {
  const [items, setItems] = useState<WarehouseAssortmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { products: catalogProducts, loading: catalogLoading } = useProductCatalog({
    activeForOps: true,
    pageSize: 2000,
  });

  const load = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/warehouses/${warehouseId}/products?pageSize=2000`);
      if (!res.ok) throw new Error(formatApiErrorBody(await res.json().catch(() => ({}))));
      const data = await res.json();
      setItems(data.items ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load assortment');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [warehouseId]);

  useEffect(() => {
    load();
  }, [load]);

  const assignedIds = useMemo(() => new Set(items.map((item) => item.productId)), [items]);

  const addOptions = useMemo(
    () => catalogProducts.filter((product) => !assignedIds.has(product.id)),
    [assignedIds, catalogProducts]
  );

  const addProducts = useCallback(
    async (productIds: string[]) => {
      const uniqueIds = Array.from(new Set(productIds.filter(Boolean)));
      if (uniqueIds.length === 0) return;

      setAdding(true);
      setError(null);
      try {
        for (const batch of chunkIds(uniqueIds, ADD_BATCH_SIZE)) {
          const res = await apiFetch(`/api/v1/warehouses/${warehouseId}/products`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ productIds: batch }),
          });
          if (!res.ok) throw new Error(formatApiErrorBody(await res.json().catch(() => ({}))));
        }
        await load({ silent: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to add products');
        throw e;
      } finally {
        setAdding(false);
      }
    },
    [load, warehouseId]
  );

  async function removeProduct(productId: string) {
    setRemovingId(productId);
    setError(null);
    const previousItems = items;
    setItems((current) => current.filter((item) => item.productId !== productId));
    try {
      const res = await apiFetch(`/api/v1/warehouses/${warehouseId}/products/${productId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(formatApiErrorBody(await res.json().catch(() => ({}))));
    } catch (e) {
      setItems(previousItems);
      setError(e instanceof Error ? e.message : 'Failed to remove product');
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={() => setBulkOpen(true)} disabled={adding}>
            <Plus className="mr-2 h-4 w-4" />
            Add products
          </Button>
          {addOptions.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {addOptions.length} product{addOptions.length === 1 ? '' : 's'} not yet assigned
            </span>
          )}
          {adding && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Adding…
            </span>
          )}
        </div>
      )}

      <WarehouseAssortmentBulkAddDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        products={addOptions}
        loading={catalogLoading}
        adding={adding}
        onAdd={addProducts}
      />

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead className="hidden md:table-cell">SKU</TableHead>
              <TableHead className="text-right">On hand</TableHead>
              <TableHead className="hidden text-right md:table-cell">Reserved</TableHead>
              {canEdit && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4} className="py-8 text-center text-muted-foreground">
                  Loading assortment…
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4} className="py-8 text-center text-muted-foreground">
                  No products assigned yet. Use Add products to assign your catalogue before a stock take.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <ProductIdentity
                      productId={item.productId}
                      name={item.product?.name ?? '—'}
                      sku={item.product?.sku}
                      size="sm"
                      product={{
                        imageUrl: item.product?.imageUrl ?? null,
                        media: item.product?.media ?? undefined,
                      }}
                    />
                  </TableCell>
                  <TableCell className="hidden font-mono text-sm md:table-cell">
                    {item.product?.sku ?? '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <FormattedNumber value={item.quantityOnHand} />
                  </TableCell>
                  <TableCell className="hidden text-right tabular-nums md:table-cell">
                    {item.quantityReserved}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        disabled={removingId === item.productId}
                        aria-label={`Remove ${item.product?.name ?? 'product'}`}
                        onClick={() => void removeProduct(item.productId)}
                      >
                        {removingId === item.productId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export default WarehouseAssortmentPanel;
